// src/components/EnhancedAssessment.js
import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useApi } from '../hooks/useApi';
import Navigation from "../components/Navigation";
import { PINNED_SIDEBAR_STICKY, PINNED_PANEL_TOP } from "../components/PinnedNavigation";
import LoadingSpinner from "../components/LoadingSpinner";
import Card from "../components/Card";
import Button from "../components/Buttons";
import { Upload, FileText, X, CheckCircle, ArrowLeft, ArrowRight, Loader, AlertCircle, Clock, Flag, Search, Lock } from "lucide-react";
import QuestionGuidanceBox from "../components/shared/QuestionGuidanceBox";
import { API_BASE_URL } from "../config";
import { useOrganizations } from "../contexts/OrganizationContext";
import { useVerticals } from "../contexts/VerticalsContext";
import { useAutoAdvance } from "../hooks/useAutoAdvance";
import { useReviewAutoSave } from "../hooks/useReviewAutoSave";
import ReviewSaveStatus from "./ReviewSaveStatus";
import AssessmentSectionSidebar from "../components/AssessmentSectionSidebar";
import { groupQuestionsByCategory, buildAssessmentSections } from "../utils/assessmentSections";
import {
    buildQuestionMap,
    collectDescendantAnswersToClear,
    collectDescendantIds,
    getVisibleQuestions,
    inheritRootCategory,
    isQuestionVisible,
    orderQuestionsWithChildren,
} from "../utils/enterpriseQuestionVisibility";
import { buildDirtyResponseEntries, queueHiddenSavedDocuments, deleteQueuedDocuments, takeQueuedDocuments, mergeQueuedDocuments, assignHiddenDocumentOwner } from "../utils/assessmentResponses";
import { filterAndSortReviewItems } from "../utils/assessmentReview";
import { withPisfControls } from "../utils/frameworkLabel";
import QuestionHistoryToggle, { QuestionHistoryPanel } from "../components/QuestionHistoryToggle";
import { useRBAC } from "../hooks/useRBAC";

// Colors for the read-only review answer badge (Answers section, isReadOnlyReview).
const ANSWER_BADGE_STYLES = {
    'Yes': "bg-green-100 text-green-800 border-green-200",
    'Partially': "bg-yellow-100 text-yellow-800 border-yellow-200",
    'No': "bg-red-100 text-red-800 border-red-200",
    'Not Applicable': "bg-gray-100 text-gray-700 border-gray-200",
};

// Helper function to build query parameters for question filtering
const buildQuestionFilterParams = (formData) => {
    const params = new URLSearchParams();
    if (formData.verticalId) params.append('verticalId', formData.verticalId);
    if (formData.regionId) params.append('regionId', formData.regionId);
    const queryString = params.toString();
    return queryString ? `?${queryString}` : '';
};

const EnterpriseAssessment = () => {
    const location = useLocation();
    const dealData = location.state || {};
    // Instead of localStorage, default to dealData.organizationId if exists
    const defaultOrgId = dealData?.organizationId || JSON.parse(localStorage.getItem("selectedOrganization"))?.organizationId;

    const apiCall = useApi();
    const { user } = useAuth();
    const { hasMinimumRoleAtOrAbove } = useRBAC();
    const navigate = useNavigate();
    const { id: urlAssessmentId } = useParams();
    const [initialized, setInitialized] = useState(false);
    const organizations = useOrganizations();
    const [respondentInfo, setRespondentInfo] = useState({ hasRespondents: false, isRespondent: false, ownerUserId: null });

    // form state - use consistent camelCase keys
    const [formData, setFormData] = useState({
        OrganizationId: defaultOrgId || "",
        dealID: dealData?.dealID || "",
        businessUnitId: dealData?.businessUnitId || "",
        dealName: dealData?.dealName || "",
        businessUnitName: dealData?.businessUnitName || "",
        dealTypeId: dealData?.dealTypeId || "",
        regionId: dealData?.regionId || "",
        verticalId: dealData?.verticalId || "",
        subVerticalId: dealData?.subVerticalId || "",
        userBaseId: dealData?.userBaseId || "",
        deploymentModelId: dealData?.deploymentModelId || "",
    });

    // Multi-respondent submit gating: any assigned respondent can submit — no "primary" concept.
    const canSubmitAssessment = !respondentInfo.hasRespondents
        || respondentInfo.isRespondent
        || respondentInfo.ownerUserId === user?.id
        || hasMinimumRoleAtOrAbove('CONTRIBUTOR', 'ORG', parseInt(formData?.OrganizationId || 0));

    const [step, setStep] = useState('questions');
    const [questions, setQuestions] = useState([]);
    const [existingStatus, setExistingStatus] = useState(null);
    // Completed via document upload — used only to word the empty read-only review state;
    // the state itself keys off having zero answers (question and document assessments
    // can co-exist, so a document assessment may still have viewable answers).
    const [isDocumentAssessment, setIsDocumentAssessment] = useState(false);
    const [answeredCount, setAnsweredCount] = useState(0);
    const [responses, setResponses] = useState({});
    const [currentQuestion, setCurrentQuestion] = useState(0);
    const [dirtyQuestions, setDirtyQuestions] = useState(new Set());
    // Bumped on each answer, note and flag edit. A save clears the question's dirty flag only if
    // no edit landed while it was in flight, so Submit still sends that edit (AX-205).
    const editVersionRef = useRef({});
    const bumpEditVersion = (qId) => {
        const key = String(qId);
        editVersionRef.current[key] = (editVersionRef.current[key] || 0) + 1;
    };
    // A review note's value when its textarea gained focus: blur saves the note only if it changed.
    const reviewNoteFocusRef = useRef({});
    // The latest submitAssessment, so Submit run after pending review saves reads the state they
    // left (dirty flags, uploaded files) rather than the click-time closure.
    const submitAssessmentRef = useRef(null);
    const [notes, setNotes] = useState({});
    const [flaggedQuestions, setFlaggedQuestions] = useState(new Set());
    // Attachments already persisted server-side (rehydrated on load), keyed by organizationQuestionId.
    // Kept separate from uploadedDocuments (raw File objects awaiting upload) since the shapes differ.
    const [savedDocuments, setSavedDocuments] = useState({});
    // Saved documents of questions a parent answer hid, deleted after the next successful save
    // (see queueHiddenSavedDocuments). A ref: only the save paths read it, never the render.
    const hiddenDocumentsQueueRef = useRef({});
    // The parent question whose answer hid each queued question, keyed by the hidden question's id.
    const hiddenDocumentsParentRef = useRef({});
    // Which already-saved documents currently have a retype request in flight, keyed by document
    // id — drives the inline spinner and disables that dropdown so a fast series of changes (see
    // handleChangeSavedDocumentType) can't fire overlapping PUTs for the same document.
    const [savingDocType, setSavingDocType] = useState({});
    // Draft free-text value for an already-saved document currently being retyped to a custom
    // ("Other") type, keyed by document id — presence of a key puts that document's retype
    // control into text-input mode instead of the preset dropdown.
    const [otherRetypeDraft, setOtherRetypeDraft] = useState({});
    const [reviewFilter, setReviewFilter] = useState('all');
    const [reviewSort, setReviewSort] = useState('none');
    const [reviewSearch, setReviewSearch] = useState('');
    // Sidebar-jump support for the review step: a click sets pendingJumpQuestionId, which the
    // effect below (defined once the review-step data is available) scrolls to and briefly
    // highlights — kept as a two-step (pending -> scroll) rather than scrolling directly in the
    // click handler so the scroll happens AFTER any filter reset has re-rendered the list (a
    // filtered-out target can't be scrolled to until the filter clears and it's back in the DOM).
    const [pendingJumpQuestionId, setPendingJumpQuestionId] = useState(null);
    const [highlightedQuestionId, setHighlightedQuestionId] = useState(null);
    // Which question the review-step sidebar treats as "active" — unlike highlightedQuestionId
    // (a ~1.5s flash on the card itself), this persists after a jump so the sidebar keeps that
    // category expanded/highlighted and keeps showing its "jump to question N" picker, letting
    // the user keep navigating within the same category instead of it collapsing right away.
    const [currentReviewQuestionId, setCurrentReviewQuestionId] = useState(null);

    // `questionCategory` is unpopulated on OrganizationQuestion in practice — the field that
    // actually carries a meaningful per-question grouping (matching the Domain breakdown on the
    // risk view) is `riskDomain`. Fall back through questionCategory/organizationQuestionDomain
    // (vertical name) before giving up and bucketing under "General". A conditional follow-up
    // then takes its top-level parent's category, so it is shown in the parent's section even when
    // its own riskDomain differs (risk reporting still uses the question's own riskDomain).
    const categorizedQuestions = useMemo(
        () => inheritRootCategory(questions.map(q => ({
            ...q,
            __category: q.riskDomain || q.questionCategory || q.organizationQuestionDomain || 'General',
        })), '__category'),
        [questions]
    );

    // Category sections made contiguous (question bank may interleave categories), so the
    // sidebar's per-category navigation lines up with the linear question stepper. Conditional
    // follow-ups are then spliced directly after their parent (same order as TPRMAssessment.js).
    // Ordering runs AFTER grouping so a child stays beneath its parent; children already share
    // their parent's __category (inheritRootCategory above).
    const structuredQuestions = useMemo(
        () => orderQuestionsWithChildren(groupQuestionsByCategory(categorizedQuestions, '__category')),
        [categorizedQuestions]
    );
    // Only the questions visible under the current answers. Everything downstream (stepper,
    // Sections sidebar, answered count, submit gate, review list) derives from this list, so a
    // hidden conditional question is never shown, counted, or required for submission.
    const orderedQuestions = useMemo(
        () => getVisibleQuestions(structuredQuestions, responses),
        [structuredQuestions, responses]
    );
    const sections = useMemo(
        () => buildAssessmentSections(orderedQuestions, {
            categoryField: '__category',
            idField: 'organizationQuestionId',
            textField: 'organizationQuestionText',
            responses,
        }),
        [orderedQuestions, responses]
    );
    // The visible list can shrink (a parent answer hides follow-ups, or a resumed assessment's
    // saved index was taken over a longer list); keep the stepper on a real question.
    useEffect(() => {
        if (orderedQuestions.length > 0 && currentQuestion > orderedQuestions.length - 1) {
            setCurrentQuestion(orderedQuestions.length - 1);
        }
    }, [orderedQuestions.length, currentQuestion]);
    // Total across ALL categories, not just the currently-displayed question — the
    // per-category sidebar lets users jump straight to the last category, so gating on
    // only the current question let assessments reach Review/Submit while earlier
    // categories were still blank.
    const answeredQuestionsCount = useMemo(
        () => orderedQuestions.filter(q => responses[String(q.organizationQuestionId)]).length,
        [orderedQuestions, responses]
    );

    // Review-step list: original question numbering is preserved even when filtered/sorted.
    const reviewQuestions = useMemo(() => filterAndSortReviewItems(
        orderedQuestions.map((q, idx) => ({ q, num: idx + 1 })),
        {
            getId: ({ q }) => String(q.organizationQuestionId),
            getText: ({ q }) => q.organizationQuestionText,
            responses,
            notes,
            flaggedQuestions,
            filter: reviewFilter,
            sort: reviewSort,
            search: reviewSearch,
        }
    ), [orderedQuestions, reviewFilter, reviewSort, reviewSearch, flaggedQuestions, notes, responses]);

    // Scrolls to (and briefly highlights) a review-step question card once it's actually in the
    // DOM. handleSidebarJumpToQuestion resets any active filter/search that could be hiding the
    // target and sets pendingJumpQuestionId; this effect retries on every reviewQuestions
    // recompute until the target element exists, so the scroll always lands after the filter
    // reset takes effect instead of firing against a stale, still-filtered list.
    useEffect(() => {
        if (!pendingJumpQuestionId) return;
        const el = document.getElementById(`review-q-${pendingJumpQuestionId}`);
        if (!el) return;
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        setHighlightedQuestionId(pendingJumpQuestionId);
        setPendingJumpQuestionId(null);
        const timer = setTimeout(() => setHighlightedQuestionId(null), 1500);
        return () => clearTimeout(timer);
    }, [pendingJumpQuestionId, reviewQuestions]);

    // Called by the review step's Sections sidebar. Clears any filter/search that would hide the
    // target category (sort is left alone — it only reorders, never hides) and queues the scroll;
    // see the effect above for why this is two steps instead of scrolling directly here.
    const handleSidebarJumpToQuestion = (questionId) => {
        setReviewFilter('all');
        setReviewSearch('');
        setPendingJumpQuestionId(questionId);
        setCurrentReviewQuestionId(questionId);
    };

    // Analysis state for async processing
    const [isAnalyzing, setIsAnalyzing] = useState(false);
    const [analysisProgress, setAnalysisProgress] = useState(0);
    const [analysisError, setAnalysisError] = useState(null);
    const [isAnalysisTimeout, setIsAnalysisTimeout] = useState(false);
    const [analyzingAssessmentId, setAnalyzingAssessmentId] = useState(null);

    useEffect(() => {
        if (initialized) return;
        let didRun = false;

        const fetchExistingAssessment = async () => {
            if (didRun) return;
            didRun = true;
            setInitialized(true);
            try {
                if (!urlAssessmentId && !formData?.OrganizationId) {
                    return;
                }

                // Use a mutable copy for hydration (formData is const from useState)
                let updatedFormData = { ...formData };

                // 1️⃣ Fetch existing partial assessment (404 means no existing assessment).
                // Only a 404 may fall through to auto-create below: a transient 401/403/timeout
                // used to look identical to "nothing exists" and produced a duplicate, unassigned
                // in-progress assessment beside the real one (the backend now also refuses to
                // insert a second current row per scope, but don't rely on that alone).
                let existing = null;
                try {
                    if (urlAssessmentId) {
                        existing = await apiCall("GET", `/enterpriseAssessments/${urlAssessmentId}/detail`);
                    } else {
                        existing = await apiCall(
                            "GET",`/enterpriseAssessments/partial-assessment?organizationId=${updatedFormData.OrganizationId}${updatedFormData.dealID ? `&dealId=${updatedFormData.dealID}` : ""}${updatedFormData.businessUnitId ? `&businessUnitId=${updatedFormData.businessUnitId}` : ""
                            }`
                        );
                    }
                } catch (partialErr) {
                    if (partialErr?.response?.status !== 404) {
                        console.error("Failed to load existing enterprise assessment", partialErr);
                        alert("Could not load the existing assessment. Please go back and try again.");
                        navigate(-1);
                        return;
                    }
                }

                // Hydrate from API response (works for both URL navigation and partial-assessment)
                if (existing) {
                    updatedFormData = {
                        ...updatedFormData,
                        OrganizationId: existing.organizationId || updatedFormData.OrganizationId,
                        dealID: existing.dealID || updatedFormData.dealID,
                        businessUnitId: existing.businessUnitId || updatedFormData.businessUnitId,
                        dealName: existing.dealName || updatedFormData.dealName,
                        businessUnitName: existing.businessUnitName || updatedFormData.businessUnitName,
                        regionId: existing.regionId || updatedFormData.regionId,
                        verticalId: existing.verticalId || updatedFormData.verticalId,
                        subVerticalId: existing.subVerticalId || updatedFormData.subVerticalId,
                    };
                    setFormData(updatedFormData);
                }

                // Backwards compatible: fall back to org's verticalId if assessment has none
                if (!updatedFormData.verticalId && updatedFormData.OrganizationId) {
                    const org = organizations.find(o =>
                        String(o.organizationId) === String(updatedFormData.OrganizationId)
                    );
                    if (org?.verticalId) {
                        updatedFormData.verticalId = org.verticalId;
                    }
                    if (org?.subVerticalId) {
                        updatedFormData.subVerticalId = org.subVerticalId;
                    }
                }

                // Country-scoped regulatory overlay (e.g. Pakistan/SBP) is resolved server-side
                // from the organization record — no country parameter is sent from the client.

                // Persist the resolved vertical fallbacks into state so later consumers
                // (e.g. handleContextSubmit's refetch) see them too. Safe to set once here — this
                // effect is guarded by `initialized`/`didRun` to run only once per mount.
                setFormData(prev => ({
                    ...prev,
                    verticalId: updatedFormData.verticalId,
                    subVerticalId: updatedFormData.subVerticalId,
                }));

                // 2️⃣ Fetch full organization question set (with filters)
                const filterParams = buildQuestionFilterParams(updatedFormData);
                const allQuestions = await apiCall("GET", `/enterpriseQuestion${filterParams}`);
                let mergedQuestions = Array.isArray(allQuestions) ? allQuestions : [];

                // 3️⃣ Load existing or create new assessment
                if (existing) {
                    setSavedAssessmentID(existing.enterpriseAssessmentID);
                    setExistingStatus(existing.enterpriseStatus || null);
                    setIsDocumentAssessment(!!existing.enterpriseAssessmentDocumentId);

                    // Load respondent roster for multi-respondent submit gating
                    try {
                        const roster = await apiCall("GET", `/enterpriseAssessments/${existing.enterpriseAssessmentID}/assignees`);
                        const assignees = roster?.assignees || [];
                        setRespondentInfo({
                            hasRespondents: assignees.length > 0,
                            isRespondent: assignees.some(a => a.userId === user?.id),
                            ownerUserId: roster?.ownerUserId ?? existing.enterpriseAssignedToUserId ?? null,
                        });
                    } catch (rosterErr) {}

                    if (!urlAssessmentId && existing.enterpriseAssessmentID) {
                        navigate(`/EnterpriseAssessment/${existing.enterpriseAssessmentID}`, { replace: true, state: location.state });
                    }

                    if (Array.isArray(existing.responses) && existing.responses.length > 0) {
                        const previousResponses = {};
                        const previousNotes = {};
                        const previousFlags = new Set();
                        const previousSavedDocs = {};
                        existing.responses.forEach((r) => {
                            const qIdStr = String(r.organizationQuestionId);
                            // Flag-only / note-only saves create rows with no answer — don't
                            // count those as answered.
                            if (r.enterpriseAssessmentResponseAnswer) {
                                previousResponses[qIdStr] = r.enterpriseAssessmentResponseAnswer;
                            }
                            if (r.enterpriseAssessmentResponseComments) {
                                previousNotes[qIdStr] = r.enterpriseAssessmentResponseComments;
                            }
                            if (r.isFlagged) {
                                previousFlags.add(qIdStr);
                            }
                            if (Array.isArray(r.documents) && r.documents.length > 0) {
                                previousSavedDocs[qIdStr] = r.documents.map((d) => ({
                                    id: d.enterpriseAssessmentSupportingDocumentId,
                                    name: d.enterpriseAttachmentName,
                                    type: d.enterpriseAttachmentType,
                                    path: d.enterpriseSupportingDocumentPath,
                                }));
                            }
                        });

                        setResponses(previousResponses);
                        setNotes(previousNotes);
                        setFlaggedQuestions(previousFlags);
                        setSavedDocuments(previousSavedDocs);
                        setAnsweredCount(Object.keys(previousResponses).length);

                        // Restore last question index
                        setCurrentQuestion(
                            existing.lastQuestionAnswerIndex != null
                                ? existing.lastQuestionAnswerIndex
                                : Math.min(Object.keys(previousResponses).length, mergedQuestions.length - 1)
                        );
                    } else {
                        setCurrentQuestion(0);
                    }
                } else {
                    // Create a new assessment automatically
                    const createPayload = {
                        DealId: updatedFormData.dealID || null,
                        BusinessUnitId: updatedFormData.businessUnitId || null,
                        OrganizationId: updatedFormData?.OrganizationId,
                        enterpriseAssessmentType: updatedFormData.assessmentType || "Initial",
                        enterpriseFramework: updatedFormData.framework || "Default",
                        enterpriseAssessmentDate: new Date().toISOString(),
                        enterpriseStatus: "in_progress",
                        ApplicationTypeId: updatedFormData.applicationTypeId || null,
                        RegionId: updatedFormData.regionId || null,
                        VerticalId: updatedFormData.verticalId || null,
                        SubVerticalId: updatedFormData.subVerticalId || null,
                        DeploymentModelId: updatedFormData.deploymentModelId || null,
                        UserBaseId: updatedFormData.userBaseId || null
                    };

                    try {
                        const created = await apiCall("POST", "/enterpriseAssessments", createPayload);
                        const newAssessmentId = created?.enterpriseAssessmentId || created?.id || null;
                        setSavedAssessmentID(newAssessmentId);
                        if (!urlAssessmentId && newAssessmentId) {
                            navigate(`/EnterpriseAssessment/${newAssessmentId}`, { replace: true, state: location.state });
                        }
                    } catch (createErr) {}

                    setExistingStatus(null);
                    setCurrentQuestion(0);
                }

                setQuestions(mergedQuestions);
                setQuestionsLoaded(true);

                // Force state refresh
                setTimeout(() => setResponses((prev) => ({ ...prev })), 100);
            } catch (err) {
                setQuestionsLoaded(true);
                setStep("questions");
            }
        };

        // Delay ensures formData/orgn load first
        const timer = setTimeout(fetchExistingAssessment, 400);
        return () => clearTimeout(timer);
    }, [formData?.dealID, formData?.OrganizationId, formData?.verticalId, formData?.regionId, urlAssessmentId]);


    // Step restoration logic
    useEffect(() => {
        // Check URL param FIRST, before any guards, so reassess navigation always works. Kept in
        // the URL (not stripped) once landed — this is what makes a hard refresh of the review URL
        // land back on review instead of falling through to the questions wizard below, which
        // decides based on existingStatus/answeredCount that are still unset on the very first
        // effect pass (this effect runs once, before the assessment fetch — which has a 400ms
        // delay — resolves).
        const urlParams = new URLSearchParams(window.location.search);
        const stepParam = urlParams.get('step');
        if (stepParam === 'review') {
            setStep("review");
            return;
        }

        // Run only after questions are loaded
        if (questions.length === 0) return;

        if (existingStatus === "analyzing") {
            setStep("analyzing");
            setIsAnalyzing(true);
            setAnalyzingAssessmentId(urlAssessmentId || savedAssessmentID);
        } else if (existingStatus === "completed") {
            if (answeredCount === 0) {
                setStep("questions");
                setCurrentQuestion(0);
            } else {
                setStep("review");
            }
        } else if (existingStatus === "in_progress") {
            setStep("questions");
            setCurrentQuestion(answeredCount || 0);
        } else {
            setStep("questions");
            setCurrentQuestion(0);
        }
    }, []); // ✅ EMPTY dependency array — runs once at mount; the URL check above (not stripped)
             // is what makes review reachable again on a later refresh, not a re-run of this effect.




    const { verticals = [] } = useVerticals() || {};

    // Per-question doc type selection — keyed by question id (like `notes`/`responses`), NOT a
    // single shared value. A single shared value here was the bug: picking "Network Diagram" on
    // Q1 left the dropdown (and any upload made without re-picking) showing "Network Diagram" on
    // every later question too, since nothing ever reset it on navigation.
    const [docTypeByQuestion, setDocTypeByQuestion] = useState({});
    const [otherDocTypeByQuestion, setOtherDocTypeByQuestion] = useState({});
    const [prefilled, setPrefilled] = useState(false);

    const [extraAttachments, setExtraAttachments] = useState([]); // Extra files not linked to a question
    const [extraAttachmentType, setExtraAttachmentType] = useState(""); // Type selector for extra attachments
    const [extraAttachmentOther, setExtraAttachmentOther] = useState(""); // If "Other" selected

    const [savedAssessmentID, setSavedAssessmentID] = useState(null);
    // Per-question audit trail (who answered/changed/uploaded/noted), grouped client-side by
    // organizationQuestionId. Fetched ONCE per assessment (an assessment can have 50-150
    // questions — a per-question fetch would be N+1 calls) and refreshed after each save so newly
    // logged entries show up without a full reload.
    const [questionHistory, setQuestionHistory] = useState({});

    const loadQuestionHistory = useCallback(async (assessmentId) => {
        if (!assessmentId) return;
        try {
            // includeChain: reassessment creates a new assessment row, so earlier rounds' answers are
            // logged under their own ids — without it a reassessed question shows no prior history.
            const data = await apiCall("GET", `/assessmentActivityLog?assessmentType=enterprise&assessmentId=${assessmentId}&includeChain=true`);
            const grouped = {};
            (Array.isArray(data) ? data : []).forEach((entry) => {
                if (entry.questionId == null) return; // whole-assessment events (assigned/completed/…), not per-question
                const key = String(entry.questionId);
                (grouped[key] ||= []).push(entry);
            });
            setQuestionHistory(grouped);
        } catch {
            setQuestionHistory({});
        }
    }, [apiCall]);

    // After a successful /responses save, move any files that were just uploaded from the
    // "pending" list (uploadedDocuments) into the "saved" list (savedDocuments) using the
    // server's own confirmation (AddResponses' response body already includes the created
    // document rows per question), and clear the pending entries for those questions. Without
    // this, a stale File object sits in uploadedDocuments forever — the file input never re-fires
    // onChange for it — so it gets resent, RE-uploaded, and RE-logged as a fresh
    // document_uploaded on every later save of that question, even one that only changes Notes.
    const applyUploadedDocuments = useCallback((saveResult, questionIds) => {
        const responses = saveResult?.responses;
        if (Array.isArray(responses)) {
            const docsByQuestion = {};
            responses.forEach((r) => {
                if (r?.documents?.length) {
                    docsByQuestion[String(r.organizationQuestionId)] = r.documents.map((d) => ({
                        id: d.enterpriseAssessmentSupportingDocumentId,
                        name: d.enterpriseAttachmentName,
                        type: d.enterpriseAttachmentType,
                    }));
                }
            });
            if (Object.keys(docsByQuestion).length > 0) {
                setSavedDocuments((prev) => {
                    const next = { ...prev };
                    Object.entries(docsByQuestion).forEach(([qId, docs]) => {
                        next[qId] = [...(next[qId] || []), ...docs];
                    });
                    return next;
                });
            }
        }
        setUploadedDocuments((prev) => {
            const next = { ...prev };
            questionIds.forEach((qId) => { next[qId] = []; });
            return next;
        });
    }, []);

    // Removes an already-saved (server-persisted) document — the pending-upload list already had
    // its own X button (removing a staged File is just client-side state), but a document that's
    // already been uploaded needs a real delete call so the DB row and blob are actually cleaned up,
    // not just hidden client-side.
    const handleRemoveSavedDocument = async (questionId, doc) => {
        const assessmentId = urlAssessmentId || savedAssessmentID;
        if (!assessmentId || !doc?.id) return;
        if (!window.confirm(`Remove "${doc.name}"? This cannot be undone.`)) return;
        const qIdStr = String(questionId);
        try {
            await apiCall("DELETE", `/enterpriseAssessments/${assessmentId}/documents/${doc.id}`);
            setSavedDocuments((prev) => ({
                ...prev,
                [qIdStr]: (prev[qIdStr] || []).filter((d) => d.id !== doc.id),
            }));
            loadQuestionHistory(assessmentId);
        } catch (err) {
            alert("Error removing document. Please try again.");
        }
    };

    // Retypes an already-saved document without re-uploading it — the per-question "Document
    // Type" dropdown above only ever tags a NEW file being picked, so a document saved on an
    // earlier visit had no way to be corrected (test-team-reported: change the type, click Next,
    // nothing saves — there was no pending file for the upload-side fix to retag, and no endpoint
    // for this case at all).
    const handleChangeSavedDocumentType = async (questionId, doc, newType) => {
        const assessmentId = urlAssessmentId || savedAssessmentID;
        if (!assessmentId || !doc?.id || !newType || newType === doc.type) return;
        if (savingDocType[doc.id]) return; // a change for this document is already in flight
        const qIdStr = String(questionId);
        setSavingDocType((prev) => ({ ...prev, [doc.id]: true }));
        try {
            const updated = await apiCall("PUT", `/enterpriseAssessments/${assessmentId}/documents/${doc.id}`, { documentType: newType });
            setSavedDocuments((prev) => ({
                ...prev,
                [qIdStr]: (prev[qIdStr] || []).map((d) =>
                    d.id === doc.id ? { ...d, type: updated?.enterpriseAttachmentType ?? newType } : d
                ),
            }));
            loadQuestionHistory(assessmentId);
        } catch (err) {
            alert("Error updating document type. Please try again.");
        } finally {
            setSavingDocType((prev) => {
                const next = { ...prev };
                delete next[doc.id];
                return next;
            });
        }
    };

    // Switches a saved document's retype control from the preset dropdown to a free-text input —
    // matching the "Other" option the upload-side Document Type dropdown already has. Doesn't
    // save on every keystroke like the upload-side one does: retyping a SAVED document is a real
    // backend PUT, not just local state, so the save is deferred to blur/Enter (commitOtherRetype).
    const startOtherRetype = (doc) => {
        const isCustom = doc.type && !["Network Diagram", "Security Policy", "Pen Test Report", "Certification"].includes(doc.type);
        setOtherRetypeDraft((prev) => ({ ...prev, [doc.id]: isCustom ? doc.type : "" }));
    };

    const commitOtherRetype = (questionId, doc) => {
        const value = (otherRetypeDraft[doc.id] || "").trim();
        setOtherRetypeDraft((prev) => {
            const next = { ...prev };
            delete next[doc.id];
            return next;
        });
        if (value) handleChangeSavedDocumentType(questionId, doc, value);
    };

    useEffect(() => {
        const assessmentId = urlAssessmentId || savedAssessmentID;
        if (assessmentId) loadQuestionHistory(assessmentId);
    }, [urlAssessmentId, savedAssessmentID, loadQuestionHistory]);

    const handleExtraAttachmentUpload = (event) => {
        const files = Array.from(event.target.files || []);
        const validFiles = files.filter(file => file.size <= 10 * 1024 * 1024); // max 10MB
        const filesWithType = validFiles.map(f => {
            f.docType = extraAttachmentType === "Other" ? extraAttachmentOther : extraAttachmentType;
            return f;
        });

        setExtraAttachments(prev => [...prev, ...filesWithType]);
        if (event.target) event.target.value = "";
    };

    const removeExtraAttachment = (index) => {
        setExtraAttachments(prev => prev.filter((_, i) => i !== index));
    };


    useEffect(() => {
        if (!dealData || prefilled) return;

        setFormData(prev => ({
            ...prev,
            dealName: dealData.dealName || prev.dealName,
            dealTypeId: dealData.dealTypeId || prev.dealTypeId,
            regionId: dealData.regionId || prev.regionId,
            verticalId: dealData.verticalId || prev.verticalId,
            subVerticalId: dealData.subVerticalId || prev.subVerticalId,
            userBaseId: dealData.userBaseId || prev.userBaseId,
            deploymentModelId: dealData.deploymentModelId || prev.deploymentModelId,
        }));

        setPrefilled(true); // prevent re-run
    }, [dealData, prefilled]);



    // If no dealData but we only have dealId → fetch details ONCE
    useEffect(() => {
        const fetchDeal = async () => {
            if (!formData.dealID || prefilled) return;

            try {
                const deal = await apiCall("GET", `/deals/${formData.dealID}`);

                if (deal) {
                    setFormData(prev => ({
                        ...prev,
                        dealName: deal.dealName || prev.dealName,
                        dealTypeId: deal.dealTypeId || prev.dealTypeId,
                        regionId: deal.regionId || prev.regionId,
                        verticalId: deal.verticalId || prev.verticalId,
                        subVerticalId: deal.subVerticalId || prev.subVerticalId,
                        userBaseId: deal.userBaseId || prev.userBaseId,
                        deploymentModelId: deal.deploymentModelId || prev.deploymentModelId,
                    }));
                    setPrefilled(true);
                }
            } catch (err) {}
        };

        fetchDeal();
    }, [formData.dealID, apiCall, prefilled]);



    const [, setRisks] = useState([]);
    const [loading, setLoading] = useState(false);
    const [questionsLoaded, setQuestionsLoaded] = useState(false);

    // Keyed by organizationQuestionId -> array of { file, organizationQuestionId }
    const [uploadedDocuments, setUploadedDocuments] = useState({});

    const isDealPreSelected = !!dealData && dealData.isNewDeal !== true;


    // ✅ Trigger when organization is changed manually
    const handleOrganizationChange = (organizationId) => {
        setFormData({
            ...formData,
            OrganizationId: organizationId,
            dealID: "",
        });
    };




    useEffect(() => {}, []);
    // Fetch questions from backend based on selected filters
    const isDisabled = !formData.OrganizationId;

    const handleContextSubmit = async () => {
        setLoading(true);
        try {
            const params = [];
            if (formData.OrganizationId) params.push(`organizationId=${formData.OrganizationId}`);
            if (formData.dealID) params.push(`dealID=${formData.dealID}`);

            const res = await apiCall("GET", `/enterpriseQuestion${buildQuestionFilterParams(formData)}`);

            setQuestions(Array.isArray(res) ? res : []);

            setResponses({});
            setCurrentQuestion(0);
            setStep("questions");
        } catch (err) {
            setQuestions([]);
        } finally {
            setLoading(false);
        }
    };


    // Extra effect to log whenever questions actually update
    useEffect(() => {}, [questions]);

    // Polling useEffect for analysis status
    useEffect(() => {
        if (!isAnalyzing || !analyzingAssessmentId) return;

        let pollCount = 0;
        const maxPolls = 60;
        const pollInterval = 5000;

        const pollStatus = async () => {
            try {
                const statusResponse = await apiCall(
                    "GET",
                    `/enterpriseAssessments/${analyzingAssessmentId}/analysis-status`
                );

                setAnalysisProgress(Math.round(statusResponse.analysisProgress) || 0);

                if (statusResponse.status?.toLowerCase() === "completed") {
                    setIsAnalyzing(false);
                    setAnalysisProgress(100);
                    try {
                        const generatedRisks = await apiCall(
                            "GET",
                            `/enterpriseRisk?EnterpriseAssessmentID=${analyzingAssessmentId}`
                        );
                        setRisks(Array.isArray(generatedRisks) ? generatedRisks : []);
                    } catch (err) {}
                    setStep("results");
                    return;
                }

                if (statusResponse.status?.toLowerCase() === "analysisfailed") {
                    setIsAnalyzing(false);
                    setAnalysisError("Analysis failed. Please try again or contact support.");
                    return;
                }

                pollCount++;
                if (pollCount >= maxPolls) {
                    setIsAnalyzing(false);
                    setIsAnalysisTimeout(true);
                    setAnalysisError("Analysis is still in progress. In some cases, this can take 10 minutes or longer. You can safely leave this page — your results will be available when analysis completes.");
                }
            } catch (err) {
                pollCount++;
                if (pollCount >= maxPolls) {
                    setIsAnalyzing(false);
                    setIsAnalysisTimeout(true);
                    setAnalysisError("Analysis is still in progress. In some cases, this can take 10 minutes or longer. You can safely leave this page — your results will be available when analysis completes.");
                }
            }
        };

        pollStatus();
        const intervalId = setInterval(pollStatus, pollInterval);
        return () => clearInterval(intervalId);
    }, [isAnalyzing, analyzingAssessmentId, apiCall]);


    // record user answers
    // Changing a parent's answer can hide conditional follow-ups. Every piece of question-scoped
    // state on those follow-ups (answer, note, flag, pending and saved files, dirty marker) is
    // dropped here, so nothing stale is resubmitted or re-rendered if the question is revealed and
    // answered again —
    // buildDirtyResponseEntries sends a dirty question's note and flag even with no answer. The
    // server reconciles the stored answers (prune, or derive No / Not Applicable) on save.
    const handleResponse = (organizationQuestionId, answer) => {
        const qIdStr = String(organizationQuestionId);
        bumpEditVersion(qIdStr);
        const nextResponses = { ...responses, [qIdStr]: answer };
        const answerIds = collectDescendantAnswersToClear(
            Number(organizationQuestionId), questions, responses, nextResponses);
        // collectDescendantAnswersToClear skips descendants with no answer; a follow-up that was
        // only noted, flagged or given a file must still lose that state when it becomes hidden.
        const byId = buildQuestionMap(questions);
        const newlyHiddenIds = collectDescendantIds(Number(organizationQuestionId), questions)
            .filter(dId => {
                const q = byId.get(dId);
                return isQuestionVisible(q, byId, responses) && !isQuestionVisible(q, byId, nextResponses);
            });
        const clearedIds = Array.from(new Set([...answerIds, ...newlyHiddenIds].map(String)));
        clearedIds.forEach(dId => delete nextResponses[dId]);

        setResponses(nextResponses);
        setDirtyQuestions(prev => {
            const next = new Set(prev);
            next.add(qIdStr);
            clearedIds.forEach(dId => next.delete(dId));
            return next;
        });
        if (clearedIds.length > 0) {
            hiddenDocumentsQueueRef.current = queueHiddenSavedDocuments(
                hiddenDocumentsQueueRef.current, clearedIds, savedDocuments);
            hiddenDocumentsParentRef.current = assignHiddenDocumentOwner(
                hiddenDocumentsParentRef.current, qIdStr, clearedIds);
            const omitIds = (prev) => {
                if (!clearedIds.some(dId => dId in prev)) return prev;
                const next = { ...prev };
                clearedIds.forEach(dId => delete next[dId]);
                return next;
            };
            setNotes(omitIds);
            setUploadedDocuments(omitIds);
            setSavedDocuments(omitIds);
            setFlaggedQuestions(prev => {
                if (!clearedIds.some(dId => prev.has(dId))) return prev;
                const next = new Set(prev);
                clearedIds.forEach(dId => next.delete(dId));
                return next;
            });
        }
        triggerAutoAdvance();
    };

    // file upload -> (client-side). Backend should handle persisting to blob storage.
    const handleDocumentUpload = (event, organizationQuestionId, docType) => {
        const files = Array.from(event.target.files || []);
        const maxFileSize = 10 * 1024 * 1024; // 10MB
        const allowedTypes = [
            "application/pdf",
            "application/msword",
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
            "image/png",
            "image/jpeg",
            "image/jpg",
            "text/plain",
            "application/vnd.ms-excel",
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        ];

        const validFiles = files.filter((file) => {
            if (file.size > maxFileSize) {
                alert(`File ${file.name} is too large. Max 10MB.`);
                return false;
            }
            if (!allowedTypes.includes(file.type)) {
                alert(`File type ${file.type} not supported for ${file.name}`);
                return false;
            }
            return true;
        });

        setUploadedDocuments((prev) => ({
            ...prev,
            [organizationQuestionId]: [
                ...(prev[organizationQuestionId] || []),
                ...files.map((f) => {
                    // Attach docType but keep original File object
                    f.docType = docType;
                    return f;
                }),
            ],
        }));

        // Mark question as dirty when files are uploaded
        setDirtyQuestions(prev => new Set(prev).add(String(organizationQuestionId)));

        if (event.target) event.target.value = "";
    };


    // Tags only the pending (not-yet-saved) files that have NO type yet when the Document Type
    // selection changes AFTER those files were picked (file-first flow), and marks the question
    // dirty so the tag actually gets sent on the next save. Files that already carry a
    // deliberately chosen type keep it, EXCEPT when the change is a continuation of the same edit
    // (replacingType: the Other free-text's previous value — each keystroke must carry pending
    // files from the earlier partial text to the fuller one). A blanket retag here previously
    // overwrote every pending
    // file's type with the latest dropdown value, so two files uploaded under different types all
    // saved as the last-selected type. No-ops when there's no pending file yet.
    const retagPendingDocuments = (qIdStr, resolvedType, replacingType) => {
        const pending = uploadedDocuments[qIdStr];
        if (!pending || pending.length === 0) return;
        setUploadedDocuments(prev => ({
            ...prev,
            [qIdStr]: prev[qIdStr].map((f) => {
                if (!f.docType || (replacingType != null && f.docType === replacingType)) f.docType = resolvedType;
                return f;
            }),
        }));
        setDirtyQuestions(prev => new Set(prev).add(qIdStr));
    };

    const removeDocument = (organizationQuestionId, index) => {
        setUploadedDocuments((prev) => ({
            ...prev,
            [organizationQuestionId]: prev[organizationQuestionId].filter((_, i) => i !== index),
        }));
    };

    // Deletes the saved documents of hidden questions once a response save has succeeded, so the
    // hide is stored in the same save that stores the parent answer. `parentQuestionId` (a save of
    // one question) limits it to the questions that question's answer hid: another question's save
    // doesn't store that answer, and if the parent's own save failed the server would keep the old
    // answer with the follow-up's documents gone. Without it (Submit, or a save of every response)
    // it drains the whole queue. Never throws.
    const deleteHiddenQuestionDocuments = async (assessmentId, parentQuestionId) => {
        const { taken, rest } = takeQueuedDocuments(hiddenDocumentsQueueRef.current,
            parentQuestionId == null ? undefined
                : (qId) => hiddenDocumentsParentRef.current[qId] === String(parentQuestionId));
        if (Object.keys(taken).length === 0) return;
        // Out of the queue before the deletes run: handleResponse may queue more meanwhile.
        hiddenDocumentsQueueRef.current = rest;
        const failed = await deleteQueuedDocuments(taken,
            (doc) => apiCall("DELETE", `/enterpriseAssessments/${assessmentId}/documents/${doc.id}`));
        hiddenDocumentsQueueRef.current = mergeQueuedDocuments(hiddenDocumentsQueueRef.current, failed);
    };

    // Save only the current question's response (if dirty)
    const saveSingleQuestion = async (organizationQuestionId, { background = false } = {}) => {
        const qIdStr = String(organizationQuestionId);
        // background: a Review & Submit edit (useReviewAutoSave). Always sent — a newer edit can
        // re-dirty the question while an earlier save of it is in flight. No full-page spinner (it
        // would replace the review list) and no alert: ReviewSaveStatus shows a failure inline.
        if (!background && !dirtyQuestions.has(qIdStr)) return; // Skip if not dirty
        const editVersion = editVersionRef.current[qIdStr];

        if (!background) setLoading(true);
        try {
            // Ensure assessment exists first
            let enterpriseAssessmentID = savedAssessmentID;
            if (!enterpriseAssessmentID) {
                const createPayload = {
                    DealId: formData.dealID || null,
                    BusinessUnitId: formData.businessUnitId || null,
                    OrganizationId: formData?.OrganizationId,
                    enterpriseAssessmentType: formData.assessmentType || "Initial",
                    enterpriseFramework: formData.framework || "Default",
                    enterpriseAssessmentDate: new Date().toISOString(),
                    enterpriseStatus: "in_progress",
                    ApplicationTypeId: formData.applicationTypeId || null,
                    RegionId: formData.regionId || null,
                    VerticalId: formData.verticalId || null,
                    SubVerticalId: formData.subVerticalId || null,
                    DeploymentModelId: formData.deploymentModelId || null,
                    UserBaseId: formData.userBaseId || null
                };
                const created = await apiCall("POST", "/enterpriseAssessments", createPayload);
                enterpriseAssessmentID = created?.enterpriseAssessmentId || created?.id || null;
                setSavedAssessmentID(enterpriseAssessmentID);
                if (!urlAssessmentId && enterpriseAssessmentID) {
                    navigate(`/EnterpriseAssessment/${enterpriseAssessmentID}`, { replace: true, state: location.state });
                }
            }

            // Update progress
            const progressPayload = {
                // Visible questions only: hidden conditional questions are neither shown nor required.
                progress: Math.round((answeredQuestionsCount / Math.max(orderedQuestions.length, 1)) * 100),
                status: "in_progress",
                LastQuestionAnswerIndex: currentQuestion
            };
            await apiCall("PUT", `/enterpriseAssessments/${enterpriseAssessmentID}/save-progress`, progressPayload);

            // Build single response payload. Always sent when dirty — the question may be
            // dirty solely from a flag/note change, which must persist even with no answer.
            const responseArray = buildDirtyResponseEntries({
                dirtyQuestionIds: [qIdStr],
                questions,
                getQuestionId: (x) => x.organizationQuestionId,
                responses,
                notes,
                flaggedQuestions,
                buildEntry: ({ questionId, answer: entryAnswer, note, isFlagged }) => ({
                    organizationQuestionId: questionId,
                    enterpriseAssessmentResponseAnswer: entryAnswer,
                    enterpriseAssessmentResponseComments: note,
                    isFlagged,
                    ApplicationId: Number(dealData.applicationId) || 0,
                    DealId: Number(dealData.dealId) || 0,
                    OrganizationId: Number(formData?.OrganizationId) || 0,
                }),
            });

            // Build FormData with only this question's files
            const formDataToSend = new FormData();
            formDataToSend.append("responsesJson", JSON.stringify(responseArray));

            const questionDocs = uploadedDocuments[qIdStr] || [];
            questionDocs.forEach((file) => {
                const docType = file.docType || "Unspecified";
                const prefixedFile = new File([file], `Q${qIdStr}_${file.name}`, {
                    type: file.type,
                });
                formDataToSend.append("files", prefixedFile);
                formDataToSend.append("fileTypes", docType);
            });

            const saveResult = await apiCall("POST", `/enterpriseAssessments/${enterpriseAssessmentID}/responses`, formDataToSend, true);
            await deleteHiddenQuestionDocuments(enterpriseAssessmentID, qIdStr);
            loadQuestionHistory(enterpriseAssessmentID);
            applyUploadedDocuments(saveResult, [qIdStr]);

            // Clear dirty flag for this question, unless it was edited again while this save was in
            // flight: that edit is not in this payload.
            if (editVersionRef.current[qIdStr] === editVersion) {
                setDirtyQuestions(prev => {
                    const next = new Set(prev);
                    next.delete(qIdStr);
                    return next;
                });
            }
        } catch (err) {
            if (background) {
                // An earlier in-flight save of this question may have cleared the dirty flag on
                // success; re-mark it so Submit still sends the change.
                setDirtyQuestions(prev => new Set(prev).add(qIdStr));
                throw err;
            }
            alert("Error saving progress. Please try again.");
            throw err; // Re-throw to block navigation
        } finally {
            if (!background) setLoading(false);
        }
    };

    // Review & Submit edits save as they are made, so a reload keeps them (AX-205).
    const { requestSave: requestReviewSave, saveStatus: reviewSaveStatus, saveErrors: reviewSaveErrors, runWhenIdle: runAfterReviewSaves } =
        useReviewAutoSave(
            (qId) => saveSingleQuestion(qId, { background: true }),
            (qId) => orderedQuestions.some((q) => String(q.organizationQuestionId) === qId),
        );

    const nextQuestion = async () => {
        const currentQId = orderedQuestions[currentQuestion]?.organizationQuestionId;
        if (currentQId) {
            try {
                await saveSingleQuestion(currentQId);
            } catch {
                return; // Block navigation on save failure
            }
        }
        setCurrentQuestion(idx => Math.min(idx + 1, Math.max(orderedQuestions.length - 1, 0)));
    };

    const prevQuestion = async () => {
        const currentQId = orderedQuestions[currentQuestion]?.organizationQuestionId;
        if (currentQId) {
            try {
                await saveSingleQuestion(currentQId);
            } catch {
                return; // Block navigation on save failure
            }
        }
        setCurrentQuestion(idx => Math.max(idx - 1, 0));
    };

    // Jump to a specific question from the section sidebar, saving the current question first.
    const jumpToQuestion = async (organizationQuestionId) => {
        const currentQId = orderedQuestions[currentQuestion]?.organizationQuestionId;
        if (currentQId && currentQId !== organizationQuestionId) {
            try {
                await saveSingleQuestion(currentQId);
            } catch {
                return; // Block navigation on save failure
            }
        }
        const idx = orderedQuestions.findIndex(q => q.organizationQuestionId === organizationQuestionId);
        if (idx >= 0) setCurrentQuestion(idx);
    };

    const toggleFlag = (organizationQuestionId) => {
        const qId = String(organizationQuestionId);
        bumpEditVersion(qId);
        setFlaggedQuestions(prev => {
            const next = new Set(prev);
            if (next.has(qId)) next.delete(qId); else next.add(qId);
            return next;
        });
        setDirtyQuestions(prev => new Set(prev).add(qId));
    };

    // Auto-advance hook (dev-only feature, toggle with Ctrl+Shift+A)
    const { triggerAutoAdvance } = useAutoAdvance({
        nextQuestion,
        currentQuestion,
        totalQuestions: orderedQuestions.length,
        loading
    });

    // create assessment, save responses, upload docs
    const submitAssessment = async () => {
        // Leaving review to submit — drop ?step=review so a reload during/after analysis
        // resumes from normal step restoration instead of being forced to review.
        window.history.replaceState({}, '', window.location.pathname);
        setLoading(true);
        try {
            // ✅ Step 1: Build assessment object
            const assessmentPayload = {
                DealId: formData.dealID,
                BusinessUnitId: formData.businessUnitId || null,
                OrganizationId: formData.OrganizationId,
                enterpriseAssessmentType: formData.assessmentType || "Initial",
                enterpriseFramework: formData.framework || "Default",
                enterpriseAssessmentDate: new Date().toISOString(),
                enterpriseProgress: 100,
                ApplicationTypeId: formData.applicationTypeId || null,

                RegionId: formData.regionId || null,
                VerticalId: formData.verticalId || null,
                SubVerticalId: formData.subVerticalId || null,

            };

            // Remove empty strings for numeric fields (optional safety)
            Object.keys(assessmentPayload).forEach(
                key => (assessmentPayload[key] === "" ? (assessmentPayload[key] = null) : null)
            );

            // ✅ Step 2: Create or update assessment
            let enterpriseAssessmentID = savedAssessmentID;
            if (!enterpriseAssessmentID) {
                const created = await apiCall("POST", "/enterpriseAssessments", assessmentPayload);
                enterpriseAssessmentID = created?.enterpriseAssessmentId || created?.id || null;
                if (!enterpriseAssessmentID) {
                    throw new Error("Assessment creation failed: no ID returned");
                }
                setSavedAssessmentID(enterpriseAssessmentID);
                if (!urlAssessmentId && enterpriseAssessmentID) {
                    navigate(`/EnterpriseAssessment/${enterpriseAssessmentID}`, { replace: true, state: location.state });
                }
            } else {
                // Include ID in payload for backend DTO binding
                assessmentPayload.EnterpriseAssessmentID = enterpriseAssessmentID;
                await apiCall("PUT", `/enterpriseAssessments/${enterpriseAssessmentID}`, assessmentPayload);
            }

            // ✅ Step 3: Build responses array — every dirty question, including ones that
            // are only flagged/annotated and have no answer yet.
            const responseArray = buildDirtyResponseEntries({
                dirtyQuestionIds: dirtyQuestions,
                questions,
                getQuestionId: (x) => x.organizationQuestionId,
                responses,
                notes,
                flaggedQuestions,
                buildEntry: ({ questionId, answer, note, isFlagged }) => ({
                    organizationQuestionId: questionId,
                    enterpriseAssessmentResponseAnswer: answer,
                    enterpriseAssessmentResponseComments: note,
                    isFlagged,
                    ApplicationId: Number(dealData.applicationId) || 0,
                    DealId: Number(dealData.dealId) || 0,
                    OrganizationId: Number(user?.organization_id) || 0,
                }),
            });

            // ✅ Step 4 & 5: Prepare FormData and save (only if there are dirty responses)
            if (responseArray.length > 0 || dirtyQuestions.size > 0) {
                const formDataToSend = new FormData();
                formDataToSend.append("responsesJson", JSON.stringify(responseArray));

                Object.entries(uploadedDocuments)
                    .filter(([qId]) => dirtyQuestions.has(String(qId)))
                    .forEach(([qId, docs]) => {
                        docs.forEach((file) => {
                            const docType = file.docType || "Unspecified";
                            formDataToSend.append(
                                "files",
                                new File([file], `Q${qId}_${file.name}`, { type: file.type })
                            );
                            formDataToSend.append("fileTypes", docType);
                        });
                    });


                const saveResult = await apiCall("POST", `/enterpriseAssessments/${enterpriseAssessmentID}/responses`, formDataToSend, true);
                loadQuestionHistory(enterpriseAssessmentID);
                applyUploadedDocuments(saveResult, [...dirtyQuestions]);

                // Clear dirty flags after successful save
                setDirtyQuestions(new Set());
            } else {}

            // The whole deletion queue, after the response save if there was one. Runs even with
            // nothing dirty, so deletes that failed after an earlier save are retried. A failure
            // stays queued and doesn't block the submission.
            await deleteHiddenQuestionDocuments(enterpriseAssessmentID);

            try {
                await apiCall("POST", `/enterpriseAssessments/${enterpriseAssessmentID}/analyze`);
                setAnalyzingAssessmentId(enterpriseAssessmentID);
                setIsAnalyzing(true);
                setAnalysisProgress(0);
                setAnalysisError(null);
                setIsAnalysisTimeout(false);
                setStep("analyzing");
            } catch (analyzeErr) {
                if (analyzeErr?.response?.status === 409) {
                    // Already analyzing - join the existing analysis
                    setAnalyzingAssessmentId(enterpriseAssessmentID);
                    setIsAnalyzing(true);
                    setAnalysisProgress(0);
                    setAnalysisError(null);
                    setIsAnalysisTimeout(false);
                    setStep("analyzing");
                } else {
                    throw analyzeErr;
                }
            }
        } catch (err) {
            alert("Error saving assessment; check console.");
        } finally {
            setLoading(false);
        }
    };
    submitAssessmentRef.current = submitAssessment;

    // fallback client-side risk generation (small)
    const generateRisks = (responsesObj, form) => {
        const out = [];
        let id = 1;
        Object.entries(responsesObj).forEach(([qId, answer]) => {
            if (answer === 'No' || answer === 'Partially') {
                const q = questions.find(x => String(x.organizationQuestionId) === String(qId));
                if (!q) return;
                let level = 'MEDIUM';
                if (q.questionCategory === 'Data Protection' && form.industry === 'Finance') level = 'CRITICAL';
                else if (q.questionCategory === 'Authentication' && answer === 'No') level = 'HIGH';

                out.push({
                    id: `risk_${id++}`,
                    name: `${q.questionCategory} Risk`,
                    category: level,
                    description: q.organizationQuestionText || '',
                    inherent_risk_rating: level,
                    monetary_value: 150000,
                    framework_reference: [q.questionFramework],
                    mitigation_controls: [],
                    status: 'identified',
                    user_rating: level
                });
            }
        });
        return out;
    };

    if (loading) return <LoadingSpinner />;

    // QUESTIONS STEP
    if (step === 'questions') {
        // Show loading spinner while questions are being fetched
        if (!questionsLoaded) {
            return <LoadingSpinner />;
        }

        // Only show "no questions" message after loading completes with 0 results
        if (!questions || questions.length === 0) {
            return (
                <div className="min-h-screen bg-gray-50">
                    <Navigation />
                    <div className="max-w-4xl mx-auto py-8 px-4">
                        <Card>
                            <div className="text-center py-8">
                                <p className="text-gray-500">No questions found for the selected context.</p>
                                <div className="mt-4">
                                    <Button onClick={() => setStep('questions')}>Back</Button>
                                </div>
                            </div>
                        </Card>
                    </div>
                </div>
            );
        }

        const currentQ = orderedQuestions[currentQuestion] || {};
        const progress = ((currentQuestion + 1) / Math.max(orderedQuestions.length, 1)) * 100;
        const currentQIdStr = String(currentQ.organizationQuestionId);
        const isCurrentFlagged = flaggedQuestions.has(currentQIdStr);

        return (
            <div className="min-h-screen bg-gray-50">
                <Navigation />
                {/* No items-start (default stretch), for the same reason as the review step: the sidebar
                    column has to be as tall as the question card for its list to stay pinned. The
                    history panel opts out with self-start so it pins at its own height. */}
                <div className="max-w-7xl mx-auto py-8 px-4 flex gap-6">
                    <div className="hidden lg:block w-64 flex-shrink-0">
                        <AssessmentSectionSidebar
                            sections={sections}
                            currentQuestionId={currentQ.organizationQuestionId}
                            onJumpToQuestion={jumpToQuestion}
                            getQuestionId={(q) => q.organizationQuestionId}
                        />
                    </div>
                    <div className="flex-1 min-w-0">
                    <Card>
                        <div className="mb-6">
                            <div className="flex justify-between items-center mb-4">
                                <h2 className="text-2xl font-bold">Organization Assessment</h2>
                                <div className="text-sm text-gray-600">Question {currentQuestion + 1} of {orderedQuestions.length}</div>
                            </div>

                            <div className="w-full bg-gray-200 rounded-full h-2">
                                <div className="bg-blue-600 h-2 rounded-full transition-all duration-300" style={{ width: `${progress}%` }} />
                            </div>
                            {currentQ.__category && (
                                <div className="mt-3 text-sm font-medium text-gray-500 uppercase tracking-wide">
                                    {currentQ.__category}
                                </div>
                            )}
                        </div>

                        <div className="mb-8">
                            <div className="flex items-center space-x-4 mb-4">
                                <span className="px-3 py-1 bg-purple-100 text-purple-800 text-sm rounded-md">
                                    {currentQ.verticalId
                                        ? (verticals.find(v => v.verticalId === currentQ.verticalId)?.verticalName || 'Base')
                                        : 'Base'}
                                </span>
                                {currentQ.questionFramework && (
                                    <span className="px-3 py-1 bg-green-100 text-green-800 text-sm rounded-md">{withPisfControls(currentQ.questionFramework, currentQ.pisfControls)}</span>
                                )}
                            </div>

                            <div className="flex items-start justify-between gap-4 mb-6">
                                <h3 className="text-xl font-semibold flex-1">{currentQ.organizationQuestionText || 'Question missing'}</h3>
                                <div className="flex items-center gap-2 flex-shrink-0">
                                    <button
                                        type="button"
                                        onClick={() => toggleFlag(currentQ.organizationQuestionId)}
                                        className={isCurrentFlagged ? "text-amber-500 hover:text-amber-600" : "text-gray-400 hover:text-gray-600"}
                                        title={isCurrentFlagged ? "Unflag this question" : "Flag this question"}
                                    >
                                        <Flag className="h-5 w-5" fill={isCurrentFlagged ? "currentColor" : "none"} />
                                    </button>
                                </div>
                            </div>

                            <QuestionGuidanceBox key={currentQ.organizationQuestionId} helpText={currentQ.questionHelp} />

                            <div className="space-y-3">
                                {['Yes', 'Partially', 'No', 'Not Applicable'].map(option => (
                                    <label key={option} className="flex items-center p-3 border rounded-lg hover:bg-gray-50 cursor-pointer">
                                        <input
                                            type="radio"
                                            name={`q-${currentQ.organizationQuestionId}`}
                                            value={option}
                                            checked={responses[String(currentQ.organizationQuestionId)] === option}
                                            onChange={(e) => handleResponse(String(currentQ.organizationQuestionId), e.target.value)}
                                            className="mr-3"
                                        />
                                        <span className="text-gray-700">{option}</span>
                                    </label>
                                ))}
                            </div>

                            <div className="mt-4">
                                <label className="block text-sm font-medium text-gray-700 mb-2">Notes (optional)</label>
                                <textarea
                                    value={notes[currentQIdStr] || ""}
                                    onChange={(e) => {
                                        setNotes(prev => ({ ...prev, [currentQIdStr]: e.target.value }));
                                        setDirtyQuestions(prev => new Set(prev).add(currentQIdStr));
                                    }}
                                    placeholder="Add context or evidence notes for this question..."
                                    className="w-full border rounded-lg p-2 text-sm"
                                    rows={3}
                                />
                            </div>
                        </div>


                        {/* Document Upload */}
                        <div className="mb-8 p-4 border-2 border-dashed border-gray-300 rounded-lg">
                            <h4 className="font-medium text-content-primary mb-3">Supporting Documents</h4>
                            <p className="text-sm text-gray-600 mb-4">
                                Upload relevant documents such as network diagrams, security policies, pen test reports, or certifications.
                            </p>

                            {/* Select Document Type */}
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Document Type
                            </label>
                            <select
                                value={docTypeByQuestion[currentQIdStr] || ""}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    setDocTypeByQuestion(prev => ({ ...prev, [currentQIdStr]: value }));
                                    if (value !== "Other") {
                                        setOtherDocTypeByQuestion(prev => ({ ...prev, [currentQIdStr]: "" }));
                                        retagPendingDocuments(currentQIdStr, value);
                                    }
                                    // Switching to "Other" retags once the free-text field below has
                                    // something typed into it (an empty "Other" label isn't useful).
                                }}
                                className="w-full border rounded-lg p-2 mb-4"
                            >
                                <option value="">-- Select Type --</option>
                                <option value="Network Diagram">Network Diagram</option>
                                <option value="Security Policy">Security Policy</option>
                                <option value="Pen Test Report">Pen Test Report</option>
                                <option value="Certification">Certification</option>
                                <option value="Other">Other</option>
                            </select>

                            {/* Show input if "Other" is selected */}
                            {docTypeByQuestion[currentQIdStr] === "Other" && (
                                <input
                                    type="text"
                                    placeholder="Please specify document type"
                                    value={otherDocTypeByQuestion[currentQIdStr] || ""}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        const prevOtherType = otherDocTypeByQuestion[currentQIdStr] || "";
                                        setOtherDocTypeByQuestion(prev => ({ ...prev, [currentQIdStr]: value }));
                                        retagPendingDocuments(currentQIdStr, value, prevOtherType);
                                    }}
                                    className="w-full border rounded-lg p-2 mb-4"
                                />
                            )}

                            {/* File Upload */}
                            <input
                                type="file"
                                id={`document-upload-${currentQ.organizationQuestionId}`}
                                multiple
                                accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.txt,.xlsx,.xls"
                                onChange={(e) =>
                                    handleDocumentUpload(
                                        e,
                                        currentQ.organizationQuestionId,
                                        docTypeByQuestion[currentQIdStr] === "Other"
                                            ? (otherDocTypeByQuestion[currentQIdStr] || "")
                                            : (docTypeByQuestion[currentQIdStr] || "")
                                    )
                                }
                                className="hidden"
                            />
                            <label
                                htmlFor={`document-upload-${currentQ.organizationQuestionId}`}
                                className="flex items-center justify-center w-full p-4 border border-gray-300 rounded-lg hover:bg-gray-50 cursor-pointer"
                            >
                                <Upload className="h-5 w-5 text-gray-400 mr-2" />
                                <span className="text-sm text-gray-600">Click to upload documents</span>
                            </label>

                            {/* Previously uploaded (server-persisted) documents */}
                            {savedDocuments[currentQ.organizationQuestionId] &&
                                savedDocuments[currentQ.organizationQuestionId].length > 0 && (
                                    <div className="mt-4">
                                        <h5 className="text-sm font-medium text-content-primary mb-2">
                                            Previously Uploaded:
                                        </h5>
                                        <div className="space-y-2">
                                            {savedDocuments[currentQ.organizationQuestionId].map((doc) => (
                                                <div
                                                    key={doc.id}
                                                    className="flex items-center justify-between p-2 bg-blue-50 rounded border border-blue-200"
                                                >
                                                    <div className="flex items-center min-w-0">
                                                        <FileText className="h-4 w-4 text-blue-600 mr-2 flex-shrink-0" />
                                                        <span className="text-sm text-blue-800 truncate">{doc.name}</span>
                                                        {/* Retype without re-uploading — corrects the type saved with the
                                                            file, or one left "Unspecified" before this fix existed. */}
                                                        {otherRetypeDraft[doc.id] !== undefined ? (
                                                            <input
                                                                type="text"
                                                                autoFocus
                                                                value={otherRetypeDraft[doc.id]}
                                                                placeholder="Specify document type"
                                                                onChange={(e) => setOtherRetypeDraft(prev => ({ ...prev, [doc.id]: e.target.value }))}
                                                                onBlur={() => commitOtherRetype(currentQ.organizationQuestionId, doc)}
                                                                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); e.target.blur(); } }}
                                                                className="ml-3 text-xs border border-blue-200 rounded px-1 py-0.5 text-blue-700 bg-white flex-shrink-0 w-32"
                                                            />
                                                        ) : (
                                                            <select
                                                                value={doc.type || ""}
                                                                onChange={(e) => {
                                                                    if (e.target.value === "Other") startOtherRetype(doc);
                                                                    else handleChangeSavedDocumentType(currentQ.organizationQuestionId, doc, e.target.value);
                                                                }}
                                                                disabled={!!savingDocType[doc.id]}
                                                                aria-busy={!!savingDocType[doc.id]}
                                                                className={`ml-3 text-xs border border-blue-200 rounded px-1 py-0.5 text-blue-700 bg-white flex-shrink-0 ${
                                                                    savingDocType[doc.id] ? 'opacity-60 cursor-not-allowed' : ''
                                                                }`}
                                                            >
                                                                <option value="">-- Type --</option>
                                                                <option value="Network Diagram">Network Diagram</option>
                                                                <option value="Security Policy">Security Policy</option>
                                                                <option value="Pen Test Report">Pen Test Report</option>
                                                                <option value="Certification">Certification</option>
                                                                <option value="Other">Other</option>
                                                                {doc.type && !["Network Diagram", "Security Policy", "Pen Test Report", "Certification"].includes(doc.type) && (
                                                                    <option value={doc.type}>{doc.type}</option>
                                                                )}
                                                            </select>
                                                        )}
                                                        {savingDocType[doc.id] && (
                                                            <Loader className="h-3.5 w-3.5 ml-1.5 text-blue-500 animate-spin flex-shrink-0" aria-label="Saving" />
                                                        )}
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleRemoveSavedDocument(currentQ.organizationQuestionId, doc)}
                                                        className="text-red-600 hover:text-red-800 flex-shrink-0 ml-2"
                                                        title="Remove document"
                                                    >
                                                        <X className="h-4 w-4" />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                            {/* Uploaded List (pending upload) */}
                            {uploadedDocuments[currentQ.organizationQuestionId] &&
                                uploadedDocuments[currentQ.organizationQuestionId].length > 0 && (
                                    <div className="mt-4">
                                        <h5 className="text-sm font-medium text-content-primary mb-2">
                                            Uploaded Documents:
                                        </h5>
                                        <div className="space-y-2">
                                            {uploadedDocuments[currentQ.organizationQuestionId].map((file, idx) => (
                                                <div
                                                    key={idx}
                                                    className="flex items-center justify-between p-2 bg-green-50 rounded border border-green-200"
                                                >
                                                    <div className="flex items-center">
                                                        <FileText className="h-4 w-4 text-green-600 mr-2" />
                                                        <span className="text-sm text-green-800">{file.name}</span>
                                                        <span className="text-xs text-gray-500 ml-2">
                                                            ({file.size ? (file.size / 1024).toFixed(1) : "0"} KB)
                                                        </span>
                                                        {file.docType && (
                                                            <span className="ml-3 text-xs text-blue-600">
                                                                [{file.docType}]
                                                            </span>
                                                        )}
                                                    </div>
                                                    <button
                                                        onClick={() => {
                                                            setUploadedDocuments((prev) => {
                                                                const copy = { ...prev };
                                                                copy[currentQ.organizationQuestionId] = copy[currentQ.organizationQuestionId].filter(
                                                                    (_, i) => i !== idx
                                                                );
                                                                return copy;
                                                            });
                                                        }}
                                                        className="text-red-600 hover:text-red-800"
                                                    >
                                                        <X className="h-4 w-4" />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                        </div>


                        {currentQuestion === orderedQuestions.length - 1 && answeredQuestionsCount < orderedQuestions.length && (
                            <div className="mb-4 flex items-center gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                                <span>
                                    Please answer every question in all categories before submitting —{" "}
                                    {orderedQuestions.length - answeredQuestionsCount} question
                                    {orderedQuestions.length - answeredQuestionsCount === 1 ? "" : "s"} remaining.
                                </span>
                            </div>
                        )}

                        <div className="flex justify-between">
                            <Button variant="secondary" onClick={prevQuestion} disabled={currentQuestion === 0 || loading}>
                                <ArrowLeft className="h-4 w-4 mr-2" /> Previous
                            </Button>

                            <div className="flex space-x-3">
                                {/* Save Progress Button */}
                                <Button
                                    type="button"
                                    variant="ghost"
                                    onClick={async () => {
                                        const currentQId = orderedQuestions[currentQuestion]?.organizationQuestionId;
                                        if (currentQId) {
                                            try {
                                                await saveSingleQuestion(currentQId);
                                                alert("Progress saved successfully ✅");
                                            } catch {
                                                // Error already shown in saveSingleQuestion
                                            }
                                        }
                                    }}
                                    disabled={loading}
                                >
                                    {loading ? "Saving..." : "Save Progress"}
                                </Button>

                                {/* Next or Review Button */}
                                {currentQuestion === orderedQuestions.length - 1 ? (
                                    <Button
                                        type="button"
                                        onClick={async () => {
                                            const currentQId = orderedQuestions[currentQuestion]?.organizationQuestionId;
                                            if (currentQId) {
                                                try {
                                                    await saveSingleQuestion(currentQId);
                                                } catch {
                                                    return; // Block navigation on save failure
                                                }
                                            }
                                            setStep("review");
                                        }}
                                        disabled={answeredQuestionsCount < orderedQuestions.length || loading}
                                        title={answeredQuestionsCount < orderedQuestions.length
                                            ? `${orderedQuestions.length - answeredQuestionsCount} question${orderedQuestions.length - answeredQuestionsCount === 1 ? "" : "s"} remaining`
                                            : ""}
                                    >
                                        Review & Submit
                                    </Button>
                                ) : (
                                    <Button
                                        type="button"
                                        onClick={nextQuestion}
                                        disabled={!responses[String(currentQ.organizationQuestionId)] || loading}
                                    >
                                        Next <ArrowRight className="h-4 w-4 ml-2" />
                                    </Button>
                                )}
                            </div>

                        </div>
                    </Card>

                    </div>

                    {/* History panel: a persistent right-side column instead of an inline
                        collapsible under Notes — inline placement meant it was easy to miss below
                        the fold, and a long log would keep stretching the page taller. Sticky +
                        internally scrollable (see QuestionHistoryPanel) so it stays visible and
                        bounded regardless of log length. */}
                    <div className={`hidden xl:block w-80 flex-shrink-0 self-start sticky ${PINNED_PANEL_TOP}`}>
                        <QuestionHistoryPanel entries={questionHistory[currentQIdStr] || []} />
                    </div>
                </div>
            </div>
        );
    }
    if (step === 'review') {
        // The ?step=review URL fast-path (Reassess, View Answers) sets this step immediately, before
        // the assessment fetch resolves — without this gate, existingStatus/questions briefly read
        // their pre-fetch defaults and the screen flashes as an empty, editable review before
        // snapping into the correct (possibly read-only) state once data arrives.
        if (!questionsLoaded) {
            // Keep the page shell (nav + background) in place instead of swapping to a bare
            // full-screen LoadingSpinner — that would flash the whole layout (nav bar, background
            // color) in and out the instant data arrives, which reads as broken rather than loading.
            return (
                <div className="min-h-screen bg-gray-50">
                    <Navigation />
                    <div className="max-w-4xl mx-auto py-8 px-4 flex items-center justify-center" style={{ minHeight: "60vh" }}>
                        <div className="animate-spin rounded-full h-16 w-16 border-2 border-transparent border-t-accent-cyan border-r-accent-purple" />
                    </div>
                </div>
            );
        }

        // A completed assessment always lands here (see step-restoration above). Reassess resets
        // EnterpriseStatus to in_progress before navigating back to this same step, so this stays
        // false for that flow — completed is the only status this view step can't be reached with
        // editing intended.
        const isReadOnlyReview = existingStatus === 'completed';

        // A completed assessment with zero answered questions (e.g. document-derived) would
        // misleadingly render every question as "Not answered". The list pages hide View Answers
        // for these, but the URL can still be deep-linked — show an explicit state instead.
        if (isReadOnlyReview && Object.keys(responses).length === 0) {
            return (
                <div className="min-h-screen bg-gray-50">
                    <Navigation />
                    <div className="max-w-4xl mx-auto py-8 px-4">
                        <Card>
                            <h2 className="text-2xl font-bold mb-6">Assessment Responses</h2>
                            <div className="mb-6 flex items-center gap-2 text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                                <FileText className="h-4 w-4 flex-shrink-0" />
                                <span>
                                    {isDocumentAssessment
                                        ? "This assessment was completed via document upload, so there are no per-question answers to display. See the extracted risks for its results."
                                        : "This assessment has no recorded answers to display."}
                                </span>
                            </div>
                            <Button variant="secondary" onClick={() => navigate(-1)}>
                                <ArrowLeft className="h-4 w-4 mr-2" /> Back
                            </Button>
                        </Card>
                    </div>
                </div>
            );
        }

        return (
            <div className="min-h-screen bg-gray-50">
                <Navigation />
                <div className="max-w-7xl mx-auto py-8 px-4 flex gap-6">
                    {/* No items-start here (default stretch): the sidebar's own content is short,
                        but the review list can run to 50-100+ questions — items-start would cap
                        this column's height at the sidebar's natural height, leaving the sticky
                        nav below with no room in its own parent to stay pinned while the (much
                        taller) question list scrolls past. Stretching this column to match the
                        list's height gives sticky the room it needs to actually stick. */}
                    <div className="hidden lg:block w-64 flex-shrink-0">
                        <AssessmentSectionSidebar
                            sections={sections}
                            currentQuestionId={currentReviewQuestionId}
                            onJumpToQuestion={handleSidebarJumpToQuestion}
                            getQuestionId={(q) => q.organizationQuestionId}
                            stickyClassName={PINNED_SIDEBAR_STICKY}
                        />
                    </div>
                    <div className="flex-1 min-w-0">
                    <Card>
                        <h2 className="text-2xl font-bold mb-6">
                            {isReadOnlyReview ? "Assessment Responses" : "Review Your Answers"}
                        </h2>

                        {isReadOnlyReview && (
                            <div className="mb-6 flex items-center gap-2 text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                                <Lock className="h-4 w-4 flex-shrink-0" />
                                <span>This assessment is completed and shown read-only. Use Reassess to make changes.</span>
                            </div>
                        )}

                        {/* Filter / Sort controls */}
                        <div className="flex flex-wrap gap-3 mb-6">
                            <div className="relative flex-1 min-w-[200px]">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                                <input
                                    type="text"
                                    value={reviewSearch}
                                    onChange={(e) => setReviewSearch(e.target.value)}
                                    placeholder="Search questions, answers, notes..."
                                    className="w-full pl-9 pr-2 py-2 text-sm border rounded-lg"
                                />
                            </div>
                            <select
                                value={reviewFilter}
                                onChange={(e) => setReviewFilter(e.target.value)}
                                className="border rounded-lg p-2 text-sm"
                            >
                                <option value="all">All answers</option>
                                <option value="Yes">Yes</option>
                                <option value="Partially">Partially</option>
                                <option value="No">No</option>
                                <option value="Not Applicable">Not Applicable</option>
                                <option value="flagged">Flagged only</option>
                                <option value="commented">Has notes</option>
                            </select>
                            <select
                                value={reviewSort}
                                onChange={(e) => setReviewSort(e.target.value)}
                                className="border rounded-lg p-2 text-sm"
                            >
                                <option value="none">Default order</option>
                                <option value="flagged-first">Flagged first</option>
                                <option value="answer">Sort by answer</option>
                            </select>
                        </div>

                        <div className="space-y-6">
                            {reviewQuestions.map(({ q, num }) => {
                                const qIdStr = String(q.organizationQuestionId);
                                const isFlagged = flaggedQuestions.has(qIdStr);
                                return (
                                <div
                                    key={q.organizationQuestionId}
                                    id={`review-q-${q.organizationQuestionId}`}
                                    className={`p-4 border rounded-lg transition-shadow ${
                                        highlightedQuestionId === q.organizationQuestionId ? 'ring-2 ring-blue-400' : ''
                                    }`}
                                >
                                    {/* Question Text + Category + Help */}
                                    <div className="flex justify-between items-start gap-4 mb-2">
                                        <h3
                                            className="font-medium flex-1 min-w-0 cursor-pointer hover:text-blue-700"
                                            onClick={() => setCurrentReviewQuestionId(q.organizationQuestionId)}
                                            title="View this question's history"
                                        >
                                            {num}. {q.organizationQuestionText}
                                        </h3>
                                        <div className="flex items-center gap-2 flex-shrink-0">
                                            <button
                                                type="button"
                                                disabled={isReadOnlyReview}
                                                onClick={() => { toggleFlag(q.organizationQuestionId); requestReviewSave(q.organizationQuestionId); }}
                                                className={
                                                    isFlagged
                                                        ? (isReadOnlyReview ? "text-amber-500 cursor-default" : "text-amber-500 hover:text-amber-600")
                                                        : (isReadOnlyReview ? "text-gray-300 cursor-default" : "text-gray-400 hover:text-gray-600")
                                                }
                                                title={isReadOnlyReview ? "" : (isFlagged ? "Unflag this question" : "Flag this question")}
                                            >
                                                <Flag className="h-5 w-5" fill={isFlagged ? "currentColor" : "none"} />
                                            </button>
                                            <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full whitespace-nowrap">
                                                {q.verticalId
                                                    ? (verticals.find(v => v.verticalId === q.verticalId)?.verticalName || 'Base')
                                                    : 'Base'}
                                            </span>
                                        </div>
                                    </div>

                                    <QuestionGuidanceBox helpText={q.questionHelp} />

                                    {/* Answers */}
                                    {isReadOnlyReview ? (
                                        // A disabled radio group renders every option in the same muted grey —
                                        // the selected one is nearly indistinguishable at a glance. Show the
                                        // saved answer as a single colored badge instead.
                                        <div className="mb-4">
                                            <span className={`inline-block px-3 py-1.5 border rounded-lg text-sm font-medium ${ANSWER_BADGE_STYLES[responses[String(q.organizationQuestionId)]] || "bg-gray-100 text-gray-500 border-gray-200"}`}>
                                                {responses[String(q.organizationQuestionId)] || "Not answered"}
                                            </span>
                                        </div>
                                    ) : (
                                        <div className="space-y-2 mb-4">
                                            {['Yes', 'Partially', 'No', 'Not Applicable'].map(option => (
                                                <label key={option} className="flex items-center p-2 border rounded-lg hover:bg-gray-50 cursor-pointer">
                                                    <input
                                                        type="radio"
                                                        name={`review-q-${q.organizationQuestionId}`}
                                                        value={option}
                                                        checked={responses[String(q.organizationQuestionId)] === option}
                                                        onChange={(e) => { handleResponse(String(q.organizationQuestionId), e.target.value); requestReviewSave(String(q.organizationQuestionId)); }}
                                                        className="mr-2"
                                                    />
                                                    <span>{option}</span>
                                                </label>
                                            ))}
                                        </div>
                                    )}

                                    {/* Notes */}
                                    {reviewSaveStatus[qIdStr] && (
                                        <div className="-mt-2 mb-3">
                                            <ReviewSaveStatus status={reviewSaveStatus[qIdStr]} error={reviewSaveErrors[qIdStr]} onRetry={() => requestReviewSave(qIdStr)} />
                                        </div>
                                    )}
                                    <div className="mb-4">
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Notes (optional)</label>
                                        <textarea
                                            value={notes[qIdStr] || ""}
                                            disabled={isReadOnlyReview}
                                            onChange={(e) => {
                                                setNotes(prev => ({ ...prev, [qIdStr]: e.target.value }));
                                                setDirtyQuestions(prev => new Set(prev).add(qIdStr));
                                                bumpEditVersion(qIdStr);
                                            }}
                                            onFocus={(e) => { reviewNoteFocusRef.current[qIdStr] = e.target.value; }}
                                            onBlur={(e) => { if (e.target.value !== reviewNoteFocusRef.current[qIdStr]) requestReviewSave(qIdStr); }}
                                            placeholder="Add context or evidence notes for this question..."
                                            className="w-full border rounded-lg p-2 text-sm"
                                            rows={2}
                                        />
                                        <QuestionHistoryToggle entries={questionHistory[qIdStr] || []} />
                                    </div>

                                    {/* Previously uploaded (server-persisted) documents */}
                                    {savedDocuments[q.organizationQuestionId] && savedDocuments[q.organizationQuestionId].length > 0 && (
                                        <div className="mb-4 p-4 border-2 border-dashed border-gray-300 rounded-lg">
                                            <h4 className="font-medium text-content-primary mb-2">Previously Uploaded</h4>
                                            <div className="space-y-2">
                                                {savedDocuments[q.organizationQuestionId].map((doc) => (
                                                    <div key={doc.id} className="flex items-center justify-between p-2 bg-blue-50 rounded border border-blue-200">
                                                        <div className="flex items-center min-w-0">
                                                            <FileText className="h-4 w-4 text-blue-600 mr-2 flex-shrink-0" />
                                                            <span className="text-sm text-blue-800 truncate">{doc.name}</span>
                                                            {isReadOnlyReview ? (
                                                                doc.type && <span className="ml-2 text-xs text-blue-600 flex-shrink-0">[{doc.type}]</span>
                                                            ) : (
                                                                <>
                                                                    {otherRetypeDraft[doc.id] !== undefined ? (
                                                                        <input
                                                                            type="text"
                                                                            autoFocus
                                                                            value={otherRetypeDraft[doc.id]}
                                                                            placeholder="Specify document type"
                                                                            onChange={(e) => setOtherRetypeDraft(prev => ({ ...prev, [doc.id]: e.target.value }))}
                                                                            onBlur={() => commitOtherRetype(q.organizationQuestionId, doc)}
                                                                            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); e.target.blur(); } }}
                                                                            className="ml-3 text-xs border border-blue-200 rounded px-1 py-0.5 text-blue-700 bg-white flex-shrink-0 w-32"
                                                                        />
                                                                    ) : (
                                                                        <select
                                                                            value={doc.type || ""}
                                                                            onChange={(e) => {
                                                                                if (e.target.value === "Other") startOtherRetype(doc);
                                                                                else handleChangeSavedDocumentType(q.organizationQuestionId, doc, e.target.value);
                                                                            }}
                                                                            disabled={!!savingDocType[doc.id]}
                                                                            aria-busy={!!savingDocType[doc.id]}
                                                                            className={`ml-3 text-xs border border-blue-200 rounded px-1 py-0.5 text-blue-700 bg-white flex-shrink-0 ${
                                                                                savingDocType[doc.id] ? 'opacity-60 cursor-not-allowed' : ''
                                                                            }`}
                                                                        >
                                                                            <option value="">-- Type --</option>
                                                                            <option value="Network Diagram">Network Diagram</option>
                                                                            <option value="Security Policy">Security Policy</option>
                                                                            <option value="Pen Test Report">Pen Test Report</option>
                                                                            <option value="Certification">Certification</option>
                                                                            <option value="Other">Other</option>
                                                                            {doc.type && !["Network Diagram", "Security Policy", "Pen Test Report", "Certification"].includes(doc.type) && (
                                                                                <option value={doc.type}>{doc.type}</option>
                                                                            )}
                                                                        </select>
                                                                    )}
                                                                    {savingDocType[doc.id] && (
                                                                        <Loader className="h-3.5 w-3.5 ml-1.5 text-blue-500 animate-spin flex-shrink-0" aria-label="Saving" />
                                                                    )}
                                                                </>
                                                            )}
                                                        </div>
                                                        {!isReadOnlyReview && (
                                                            <button
                                                                type="button"
                                                                onClick={() => handleRemoveSavedDocument(q.organizationQuestionId, doc)}
                                                                className="text-red-600 hover:text-red-800 flex-shrink-0 ml-2"
                                                                title="Remove document"
                                                            >
                                                                <X className="h-4 w-4" />
                                                            </button>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Question Attachments */}
                                    {uploadedDocuments[q.organizationQuestionId] && uploadedDocuments[q.organizationQuestionId].length > 0 && (
                                        <div className="mb-4 p-4 border-2 border-dashed border-gray-300 rounded-lg">
                                            <h4 className="font-medium text-content-primary mb-2">Attachments</h4>
                                            <div className="space-y-2">
                                                {uploadedDocuments[q.organizationQuestionId].map((file, fidx) => (
                                                    <div key={fidx} className="flex items-center justify-between p-2 bg-green-50 rounded border border-green-200">
                                                        <div className="flex items-center">
                                                            <FileText className="h-4 w-4 text-green-600 mr-2" />
                                                            <span className="text-sm text-green-800">{file.name}</span>
                                                            {file.docType && <span className="ml-2 text-xs text-blue-600">[{file.docType}]</span>}
                                                        </div>
                                                        {!isReadOnlyReview && (
                                                            <button onClick={() => removeDocument(q.organizationQuestionId, fidx)} className="text-red-600 hover:text-red-800">
                                                                <X className="h-4 w-4" />
                                                            </button>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                                );
                            })}
                        </div>

                        {/* Extra Attachments Section — not offered read-only: nothing is loaded into
                            extraAttachments on fetch (it only ever holds files staged in this session),
                            so there's nothing to view here for a freshly-opened completed assessment. */}
                        {!isReadOnlyReview && (
                        <div className="mt-8 p-4 border-2 border-dashed border-gray-300 rounded-lg">
                            <h4 className="font-medium text-content-primary mb-2">Extra Supporting Documents</h4>
                            <p className="text-sm text-gray-600 mb-4">
                                Upload any additional files that support the overall assessment.
                            </p>

                            {/* Document Type Selector */}
                            <select
                                value={extraAttachmentType}
                                onChange={(e) => {
                                    setExtraAttachmentType(e.target.value);
                                    if (e.target.value !== "Other") setExtraAttachmentOther("");
                                }}
                                className="w-full border rounded-lg p-2 mb-4"
                            >
                                <option value="">-- Select Document Type --</option>
                                <option value="Network Diagram">Network Diagram</option>
                                <option value="Security Policy">Security Policy</option>
                                <option value="Pen Test Report">Pen Test Report</option>
                                <option value="Certification">Certification</option>
                                <option value="Other">Other</option>
                            </select>

                            {extraAttachmentType === "Other" && (
                                <input
                                    type="text"
                                    placeholder="Specify document type"
                                    value={extraAttachmentOther}
                                    onChange={(e) => setExtraAttachmentOther(e.target.value)}
                                    className="w-full border rounded-lg p-2 mb-4"
                                />
                            )}

                            {/* File Upload */}
                            <input
                                type="file"
                                id="extra-attachments-upload"
                                multiple
                                accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.txt,.xlsx,.xls"
                                onChange={handleExtraAttachmentUpload}
                                className="hidden"
                            />
                            <label
                                htmlFor="extra-attachments-upload"
                                className="flex items-center justify-center w-full p-3 border border-gray-300 rounded-lg hover:bg-gray-50 cursor-pointer"
                            >
                                <Upload className="h-5 w-5 text-gray-400 mr-2" />
                                <span className="text-sm text-gray-600">Click to upload extra files</span>
                            </label>

                            {/* Display uploaded extra files */}
                            {extraAttachments.length > 0 && (
                                <div className="mt-4 space-y-2">
                                    {extraAttachments.map((file, idx) => (
                                        <div key={idx} className="flex items-center justify-between p-2 bg-green-50 rounded border border-green-200">
                                            <div className="flex items-center">
                                                <FileText className="h-4 w-4 text-green-600 mr-2" />
                                                <span className="text-sm text-green-800">{file.name}</span>
                                                {file.docType && <span className="ml-2 text-xs text-blue-600">[{file.docType}]</span>}
                                            </div>
                                            <button onClick={() => removeExtraAttachment(idx)} className="text-red-600 hover:text-red-800">
                                                <X className="h-4 w-4" />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        )}

                        {!isReadOnlyReview && answeredQuestionsCount < orderedQuestions.length && (
                            <div className="mt-6 flex items-center gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                                <span>
                                    {orderedQuestions.length - answeredQuestionsCount} question
                                    {orderedQuestions.length - answeredQuestionsCount === 1 ? "" : "s"} still{" "}
                                    {orderedQuestions.length - answeredQuestionsCount === 1 ? "needs" : "need"} an answer before you can submit.
                                </span>
                            </div>
                        )}

                        {/* Navigation Buttons */}
                        <div className="flex justify-between mt-8">
                            {isReadOnlyReview ? (
                                <Button variant="secondary" onClick={() => navigate(-1)}>
                                    <ArrowLeft className="h-4 w-4 mr-2" /> Back
                                </Button>
                            ) : (
                                <>
                                    <Button variant="secondary" onClick={() => runAfterReviewSaves(() => {
                                        // Editing resumes — drop ?step=review so a reload lands back on questions
                                        // instead of being forced to review. Waits for pending review saves, so a
                                        // questions-step save can't race one still in flight (ignored while a
                                        // Submit is waiting).
                                        window.history.replaceState({}, '', window.location.pathname);
                                        setStep('questions');
                                    })}>
                                        <ArrowLeft className="h-4 w-4 mr-2" /> Back to Questions
                                    </Button>
                                    <Button
                                        onClick={() => runAfterReviewSaves(() => submitAssessmentRef.current())}
                                        disabled={isAnalyzing || !canSubmitAssessment || answeredQuestionsCount < orderedQuestions.length}
                                        title={
                                            isAnalyzing ? "Assessment is being analyzed"
                                            : !canSubmitAssessment ? "Only an assigned respondent can submit this assessment"
                                            : answeredQuestionsCount < orderedQuestions.length
                                                ? `${orderedQuestions.length - answeredQuestionsCount} question${orderedQuestions.length - answeredQuestionsCount === 1 ? "" : "s"} still need an answer`
                                                : ""
                                        }
                                    >
                                        Submit Assessment
                                    </Button>
                                </>
                            )}
                        </div>
                    </Card>

                    </div>

                    {/* History panel: click a question's title (above) to view it here — mirrors
                        the one-at-a-time step's right-side panel. The `sticky` class has to sit
                        on an INNER div, not this outer w-80 column itself — same reason the
                        Sections sidebar's sticky lives on its inner <nav>, not its own w-64
                        column: this outer column gets stretched to the full (very long) question
                        list's height by the flex row (no items-start, see above), so if IT were
                        the sticky element, it would already span the whole scrollable area and
                        have no room left to "chase" the viewport — the panel would just sit
                        wherever the row started, near the top of the page, instead of tracking
                        scroll position the way the sidebar does. */}
                    <div className="hidden xl:block w-80 flex-shrink-0">
                        <div className={`sticky ${PINNED_PANEL_TOP}`}>
                            {currentReviewQuestionId != null ? (
                                <QuestionHistoryPanel entries={questionHistory[String(currentReviewQuestionId)] || []} />
                            ) : (
                                <div className="bg-white border border-gray-200 rounded-lg p-3 text-xs text-gray-400">
                                    Click a question above to view its history here.
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // ANALYZING STEP
    if (step === "analyzing") {
        return (
            <div className="min-h-screen bg-gray-50">
                <Navigation />
                <div className="max-w-4xl mx-auto py-8 px-4">
                    <Card>
                        <div className="text-center py-12">
                            {analysisError ? (
                                <>
                                    {isAnalysisTimeout ? (
                                        <Clock className="h-16 w-16 text-amber-500 mx-auto mb-6" />
                                    ) : (
                                        <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-6" />
                                    )}
                                    <h2 className="text-2xl font-bold text-gray-900 mb-4">
                                        {isAnalysisTimeout ? "Analysis Taking Longer Than Expected" : "Analysis Failed"}
                                    </h2>
                                    <p className="text-gray-600 mb-8">{analysisError}</p>
                                    <div className="flex justify-center">
                                        <Button
                                            variant="secondary"
                                            onClick={() => navigate('/EnterpriseDashboard')}
                                        >
                                            View Dashboard
                                        </Button>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div className="relative">
                                        <Loader className="h-16 w-16 text-blue-600 mx-auto mb-6 animate-spin" />
                                    </div>
                                    <h2 className="text-2xl font-bold text-gray-900 mb-4">Analyzing Assessment</h2>
                                    <p className="text-gray-600 mb-2">
                                        Our AI is analyzing your responses and generating risk insights.
                                    </p>
                                    <p className="text-gray-600 mb-6">
                                        This typically takes a few minutes, but in some cases may take 10 minutes or longer.
                                    </p>
                                    <div className="max-w-md mx-auto mb-4">
                                        <div className="flex justify-between items-center mb-2">
                                            <span className="text-sm text-gray-500">Progress</span>
                                            <span className="text-sm font-medium text-gray-700">{analysisProgress}%</span>
                                        </div>
                                        <div className="w-full bg-gray-200 rounded-full h-3">
                                            <div
                                                className="bg-blue-600 h-3 rounded-full transition-all duration-500"
                                                style={{ width: `${analysisProgress}%` }}
                                            />
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    </Card>
                </div>
            </div>
        );
    }

    // RESULTS STEP
    {/*
    if (step === 'results') {
        return (
            <div className="min-h-screen bg-gray-50">
                <Navigation />
                <div className="max-w-6xl mx-auto py-8 px-4">
                    <div className="mb-8">
                        <h2 className="text-3xl font-bold">Risk Assessment Results</h2>
                        <p className="text-gray-600 mt-2">Comprehensive analysis for {formData.applicationName}</p>
                    </div>
                    <RiskResults risks={risks} setRisks={setRisks} />
                </div>
            </div>
        );
    }
    */}
    if (step === 'results') {
        // Navigate to AssessmentRisksView page after save
        navigate(`/EnterpriseAssessments/${savedAssessmentID}/risks`);

        return null; // Stop rendering current component
    }

    return null;
};

export default EnterpriseAssessment;
